import React, { useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DocThumb } from "@/components/DocThumb";
import { toast } from "sonner";
import { Camera, Check, ClipboardCheck, Loader2, RotateCcw, AlertTriangle, X, Sparkles, MapPin, FileText } from "lucide-react";

const STATUS = {
  pending: { label: "Offen", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  confirmed: { label: "Vorhanden", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  check: { label: "Prüfen", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  missing: { label: "Fehlt", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

function PositionRow({ p, companyId, busy, onMark, onPhoto, onPreview }) {
  const fileRef = useRef(null);
  const s = STATUS[p.check_status] || STATUS.pending;
  const done = p.check_status === "confirmed";
  return (
    <li className={`flex gap-3 p-3 rounded-lg border transition-colors ${done ? "border-emerald-200 bg-emerald-50/50" : p.check_status === "check" ? "border-amber-300 bg-amber-50/50" : p.check_status === "missing" ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`} data-testid={`stocktake-row-${p.item_id}`}>
      {p.has_photo ? <DocThumb url={`/companies/${companyId}/inventory/${p.item_id}/photo`} title={p.name} onOpen={() => onPreview(p)} className="h-14 w-14 shrink-0" testid={`stocktake-thumb-${p.item_id}`} />
        : <div className="h-14 w-14 shrink-0 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-slate-300"><Camera className="h-4 w-4" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{p.name}</div>
            <div className="text-xs text-slate-500 truncate">{[p.brand, p.model].filter(Boolean).join(" ") || p.category || "—"} · Menge {p.quantity}</div>
          </div>
          <Badge className={`shrink-0 border text-[10px] ${s.cls}`} data-testid={`stocktake-status-${p.item_id}`}>{s.label}</Badge>
        </div>
        {p.ai && (
          <p className={`mt-1 text-[11px] inline-flex items-center gap-1 ${p.check_status === "check" ? "text-amber-800" : "text-slate-500"}`} data-testid={`stocktake-ai-${p.item_id}`}>
            <Sparkles className="h-3 w-3" />{p.ai.match === null ? p.ai.seen : p.ai.match ? `KI: passt (${Math.round((p.ai.confidence || 0) * 100)} %) – ${p.ai.seen}` : `KI: passt nicht sicher – gesehen: ${p.ai.seen || "unklar"}`}
          </p>
        )}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {done ? (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onMark(p, "pending")} data-testid={`stocktake-undo-${p.item_id}`}><RotateCcw className="h-3 w-3 mr-1" />Zurücksetzen</Button>
          ) : (
            <Button size="sm" className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => onMark(p, "confirmed")} data-testid={`stocktake-confirm-${p.item_id}`}><Check className="h-3 w-3 mr-1" />Vorhanden</Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onPhoto(p, e.target.files[0]); e.target.value = ""; }} data-testid={`stocktake-photo-input-${p.item_id}`} />
          <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`stocktake-photo-${p.item_id}`}>
            {busy === p.item_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}Foto
          </Button>
          {p.check_status !== "missing" && !done && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-700 hover:text-rose-800" disabled={busy} onClick={() => onMark(p, "missing")} data-testid={`stocktake-missing-${p.item_id}`}><X className="h-3 w-3 mr-1" />Fehlt</Button>
          )}
        </div>
      </div>
    </li>
  );
}

export function StocktakeDialog({ companyId, stocktake, onChange, onClose, onPreview }) {
  const [st, setSt] = useState(stocktake);
  const [busy, setBusy] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const base = `/companies/${companyId}/inventory/stocktakes/${st.id}`;
  const apply = (data) => { setSt(data); onChange?.(data); };

  const mark = async (p, status) => {
    setBusy(p.item_id);
    try { const r = await apiClient.post(`${base}/items/${p.item_id}/mark`, { status }); apply(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen."); }
    finally { setBusy(null); }
  };
  const photo = async (p, file) => {
    setBusy(p.item_id);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await apiClient.post(`${base}/items/${p.item_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(r.data);
      const res = r.data.last_result;
      if (res?.status === "confirmed") toast.success(`«${p.name}» per Foto bestätigt – KI: ${res.ai?.seen || "passt"}`);
      else toast.warning(`«${p.name}»: Foto gespeichert, KI unsicher – bitte prüfen (${res?.ai?.seen || "unklar"})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Foto konnte nicht verarbeitet werden."); }
    finally { setBusy(null); }
  };
  const close = async () => {
    setClosing(true);
    try { const r = await apiClient.post(`${base}/close`); apply(r.data); setConfirmClose(false); toast.success(`Inventur abgeschlossen – ${r.data.summary?.confirmed} bestätigt, ${r.data.summary?.retired} ausgeschieden.${r.data.protocol ? " Protokoll im Dokumentenordner abgelegt." : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Abschluss fehlgeschlagen."); }
    finally { setClosing(false); }
  };
  const downloadProtocol = async () => {
    try {
      const r = await apiClient.get(`${base}/protocol.pdf`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = st.protocol?.file_name || "Inventur-Protokoll.pdf"; a.click();
    } catch { toast.error("Protokoll konnte nicht geladen werden."); }
  };
  const cancel = async () => {
    if (!window.confirm("Inventur verwerfen? Die Haken gehen verloren, Fotos bleiben an den Artikeln.")) return;
    try { await apiClient.delete(base); toast.success("Inventur verworfen."); onClose(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Verwerfen fehlgeschlagen."); }
  };

  const c = st.counts || {};
  const open = (c.total || 0) - (c.confirmed || 0);
  const pct = c.total ? Math.round(((c.confirmed || 0) / c.total) * 100) : 0;
  const order = { check: 0, pending: 1, missing: 2, confirmed: 3 };
  const positions = [...(st.positions || [])].sort((a, b) => (order[a.check_status] ?? 1) - (order[b.check_status] ?? 1));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] flex flex-col" data-testid="stocktake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-teal-700" />Inventur · <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{st.location_name}</span></DialogTitle>
          <DialogDescription>Jede Position per Haken oder Foto als «vorhanden» bestätigen. Beim Abschluss werden nicht bestätigte Positionen als ausgeschieden gesetzt.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3" data-testid="stocktake-progress">
          <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
          <span className="text-sm font-medium text-slate-800" data-testid="stocktake-progress-text">{c.confirmed || 0} / {c.total || 0} bestätigt</span>
          {c.check > 0 && <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px]">{c.check} prüfen</Badge>}
          {c.missing > 0 && <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[10px]">{c.missing} fehlt</Badge>}
        </div>
        <ul className="space-y-2 overflow-y-auto flex-1 pr-1" data-testid="stocktake-list">
          {positions.map((p) => <PositionRow key={p.item_id} p={p} companyId={companyId} busy={busy} onMark={mark} onPhoto={photo} onPreview={onPreview} />)}
        </ul>
        {st.status === "closed" ? (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="stocktake-summary">
            <span>Abgeschlossen am {new Date(st.closed_at).toLocaleDateString("de-CH")} · {st.summary?.confirmed} bestätigt ({st.summary?.by_photo} per Foto) · {st.summary?.retired} ausgeschieden</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadProtocol} data-testid="stocktake-protocol-download"><FileText className="h-4 w-4 mr-1" />PDF-Protokoll</Button>
              <Button size="sm" onClick={() => onClose(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="stocktake-done">Fertig</Button>
            </div>
          </div>
        ) : confirmClose ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="stocktake-close-confirm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <b>{open} Position{open === 1 ? "" : "en"}</b> {open === 1 ? "ist" : "sind"} nicht bestätigt und {open === 1 ? "wird" : "werden"} beim Abschluss als <b>ausgeschieden</b> gesetzt (später wieder aktivierbar).
              <div className="mt-2 flex gap-2">
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={close} disabled={closing} data-testid="stocktake-close-yes">{closing && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Jetzt abschliessen</Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmClose(false)} data-testid="stocktake-close-no">Weiter prüfen</Button>
              </div>
            </div>
          </div>
        ) : (
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={cancel} className="text-slate-500" data-testid="stocktake-cancel">Inventur verwerfen</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onClose(false)} data-testid="stocktake-pause">Später fortsetzen</Button>
              <Button onClick={() => (open > 0 ? setConfirmClose(true) : close())} disabled={closing} className="bg-teal-700 hover:bg-teal-800" data-testid="stocktake-close">
                {closing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1" />}Inventur abschliessen
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
